import React, {
    lazy,
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Panel } from '@/components/ui/panel';
// the store module only: the feature barrel also carries the flow's dialogs,
// which App mounts.
import { useFlowStore } from '@/features/flow/store';
import { bandsFor, LiqMapLegend, useLiqMap } from '@/features/liqMap';
import { OnboardingGate } from '@/features/onboarding';
import { usePriceFeed } from '@/features/priceFeed';
import {
    computeZones,
    orientLevels,
    quantiseSpot,
    SrLevelsLegend,
    useCandles,
} from '@/features/srLevels';
import {
    findMarket,
    useTokenPairLeverageDetail,
    useTokenPairStore,
    useTokenPairs,
} from '@/features/tokenPairs';
import {
    AuthErrorBanner,
    useAuth,
    useSpeedBalance,
    useTradeExecution,
    useWallet,
} from '@/features/wallet';
import { useIsMobile, useMediaQuery, useViewportInsets } from '@/lib/hooks';

import CommandPalette from './components/CommandPalette';
import CustodialPanelContainer from './components/CustodialPanelContainer';
import Header from './components/Header';
import MarketsSidebar from './components/MarketsSidebar';
import MobileControlPanel from './components/MobileControlPanel';
import SpeedChartHeader from './components/SpeedChartHeader';
import { PnlTag, WinFrame } from './components/SpeedOverlays';
import SpeedResultFx from './components/SpeedResultFx';
import SpeedTerminal from './components/SpeedTerminal';
import SpeedTradeRail from './components/SpeedTradeRail';
import TradeResultOverlay from './components/TradeResultOverlay';
import UntrackedExposureCard from './components/UntrackedExposureCard';
import TradingViewChart, {
    type ChartPositionMarks,
} from './components/TradingViewChart';
import TrendChart from './components/TrendChart';
import { useAgentPruneDetection } from './hooks/useAgentPruneDetection';
import { useAgentReactivation } from './hooks/useAgentReactivation';
import { useAllLivePositions } from './hooks/useAllLivePositions';
import { useBetClampEffect } from './hooks/useBetClampEffect';
import { useBuilderFeeApproval } from './hooks/useBuilderFeeApproval';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useLivePosition } from './hooks/useLivePosition';
import { useMaintenanceRate } from './hooks/useMaintenanceRate';
import { useModeTransitionReset } from './hooks/useModeTransitionReset';
import { useOpenTradeForCoin } from './hooks/useOpenTradeForCoin';
import { usePriceConfig } from './hooks/usePriceConfig';
import { usePriceTracking } from './hooks/usePriceTracking';
import { useDemoLimitFills } from './hooks/useDemoLimitFills';
import { useRealTradeActions } from './hooks/useRealTradeActions';
import { useRestingOrder } from './hooks/useRestingOrder';
import { useResetOnPairSwitch } from './hooks/useResetOnPairSwitch';
import { useSyncActiveTradesToStore } from './hooks/useSyncActiveTradesToStore';
import { useTradeNotificationHandler } from './hooks/useTradeNotificationHandler';
import { useTradeReconciliation } from './hooks/useTradeReconciliation';
import { useTradingStoreSync } from './hooks/useTradingStoreSync';
import { selectActiveTradeVisual, useTradingStore } from './store';
import { estimateLiquidationPrice } from './utils/pnl';
import { resolveTriggerLevels } from './utils/positionTriggers';
import { triggerPriceFrom } from './utils/triggerSetting';

type ChartMode = 'live' | 'candles';
const CHART_MODE_STORAGE_KEY = 'snappy.chartMode.v1';
const LIQ_MAP_STORAGE_KEY = 'snappy.liqMap.v1';

// Stable style identity so the memoized SpeedTerminal isn't re-rendered by a
// fresh inline object every tick. The terminal area holds the untracked-exposure
// warning above the table, so the style now lands on their shared wrapper —
// SpeedTerminal keeps its own fixed height inside it, and the `terminal` grid row
// is `auto`, so the warning grows the row instead of overflowing it.
const TERMINAL_GRID_STYLE = { gridArea: 'terminal' } as const;

// The browser wallet flow (V2) surfaces. Lazy, so a V1 session never downloads
// them or the Hyperliquid engine behind them; mounted only while V2 is in use.
// Its banner (V2FundsBanner) renders inside SpeedTradeRail / MobileControlPanel,
// where the V1 account banners sit.
const BrowserWalletDialog = lazy(
    async () =>
        import('@/features/browserWallet/components/BrowserWalletDialog'),
);
const BrowserWalletPanel = lazy(
    async () => import('@/features/browserWallet/funds/BrowserWalletPanel'),
);

const Trading: React.FC = () => {
    // ── Responsive layout ────────────────────────────────────────────────
    // The three-column layout (markets rail / chart / trade rail) engages
    // from 1024px; below that the header strip, chart and mobile controls
    // stack.
    const isMobile = useIsMobile();
    // The terminal owns the whole screen and never scrolls, so a soft keyboard
    // has to shrink it rather than cover it. Scoped here deliberately: the
    // landing page scrolls, and its own scrolling would fight this.
    useViewportInsets();
    const showRail = useMediaQuery('(min-width: 1024px)');

    // ── Chart type (live physics line vs TradingView candles) ────────────
    const [chartMode, setChartModeState] = useState<ChartMode>(() =>
        typeof window !== 'undefined' &&
        window.localStorage.getItem(CHART_MODE_STORAGE_KEY) === 'candles'
            ? 'candles'
            : 'live',
    );
    const setChartMode = useCallback((mode: ChartMode) => {
        setChartModeState(mode);
        try {
            window.localStorage.setItem(CHART_MODE_STORAGE_KEY, mode);
        } catch {
            // storage unavailable — mode just won't persist
        }
    }, []);
    const showCandles = chartMode === 'candles';

    // ── Market (coin) and network from global store ──────────────────────
    const selectedPair = useTokenPairStore((state) => state.selectedPair);
    const selectedCoin = useTokenPairStore((state) => state.selectedCoin);
    const networkId = useTokenPairStore((state) => state.networkId);
    const leverage = useTradingStore((s) => s.leverage);

    // ── Real-time price feed via WebSocket ───────────────────────────────
    const {
        price: feedPrice,
        connectionState,
        latency,
        isStale,
    } = usePriceFeed(networkId, selectedPair);

    const {
        currentValue,
        targetValue,
        currentValueCoin,
        priceFlash,
        handleCurrentValueChange,
    } = usePriceTracking(feedPrice, selectedCoin);

    const priceConfig = usePriceConfig(targetValue);

    // With the TradingView chart mounted, the physics chart (which normally
    // drives currentValue via onCurrentValueChange) is unmounted — feed the
    // WebSocket price straight through so balances/PnL keep updating.
    const lastSyncedPriceRef = useRef(targetValue);
    useEffect(() => {
        if (!showCandles) return;
        handleCurrentValueChange(
            targetValue,
            targetValue - lastSyncedPriceRef.current,
        );
        lastSyncedPriceRef.current = targetValue;
    }, [showCandles, targetValue, handleCurrentValueChange]);

    // ── Runtime Mode & Auth ──────────────────────────────────────────────
    const { runtimeMode, authError, token, sessionKey } = useAuth();
    const isRealMode = runtimeMode === 'real';
    // V1 (custodial wallet) and V2 (browser wallet) mount different account
    // surfaces; null (a social identity still resolving) counts as V1.
    const isBrowserWalletFlow = useFlowStore((state) => state.active === 'v2');
    // The custodial balance is a read of the user's OWN wallet, authorized by the app token alone —
    // the factions client carries no cookies and no trading proof. Gating its DISPLAY on real mode
    // made it wait for trading authority, which waits for the whole session handshake plus a possible
    // 5s token-exchange retry: the number sat in the cache for 10-15s while the UI rendered 0.
    // Trading itself stays gated on isRealMode; only showing the balance does not.
    const hasAppToken = !!token;
    const { address } = useWallet();

    // ── Trade Execution (Real Mode) ──────────────────────────────────────
    const tradeExecution = useTradeExecution();

    // ── Market metadata ──────────────────────────────────────────────────
    const { data: marketsData, isError: isMarketsError } =
        useTokenPairs(networkId);
    const hasMarketData = useMemo(
        () =>
            !!findMarket(marketsData?.markets, selectedCoin) && !isMarketsError,
        [marketsData, selectedCoin, isMarketsError],
    );
    const selectedMarket = useMemo(
        () => findMarket(marketsData?.markets, selectedCoin),
        [marketsData, selectedCoin],
    );

    // Heal a case-mangled selection at its single source. Lossy writers (see
    // findMarket) can persist "xyz:tsla" for the canonical "xyz:TSLA"; rewriting
    // the store once the markets list is in repairs localStorage and every
    // downstream consumer at the same time. The !== guard makes it a one-shot:
    // after the rewrite the exact-case match succeeds and this stops firing.
    useEffect(() => {
        if (!selectedMarket || selectedMarket.coin === selectedCoin) return;
        useTokenPairStore.getState().setSelectedCoin(selectedMarket.coin);
    }, [selectedMarket, selectedCoin]);
    const isIsolatedOnly = selectedMarket?.onlyIsolated === true;
    const { data: collateralDetail } = useTokenPairLeverageDetail(
        networkId,
        selectedPair,
        leverage,
    );

    // ── Speed-trading balance (Real Mode) ────────────────────────────────
    // enabled by the app token, not by real mode: the query is disabled anyway without a token
    // (factionsApi is null), and gating it on real mode delayed the first fetch behind the handshake.
    const speedBalance = useSpeedBalance();

    // ── Derived balance values for sync ──────────────────────────────────
    const demoBalance = useTradingStore((s) => s.demoBalance);
    const numericBalance = speedBalance.numericTotal ?? 0;

    // an app token is enough to show the real balance; without one there is nothing to show but demo.
    const showsCustodialBalance = isRealMode || hasAppToken;
    const balance = showsCustodialBalance ? numericBalance : demoBalance;
    // only what already sits on HyperCore can back a fast open: the backend never moves the custodial /
    // adapter (HyperEVM) part during an open, and Hyperliquid trims the fill to the margin it finds
    // (trade 481: $42.29 offered, $36.52 on core, position opened with $36.38). the total stays the
    // displayed balance; the input cap and the shortfall banner use the tradeable part.
    const numericTradeable = showsCustodialBalance
        ? Math.min(
              numericBalance,
              speedBalance.hyperCoreWithdrawable ?? numericBalance,
          )
        : demoBalance;
    const balanceForInputCap = numericTradeable;
    const custodialAvailable = showsCustodialBalance ? numericTradeable : 0;

    useEffect(() => {
        const store = useTradingStore.getState();
        if (!collateralDetail) {
            store.clearLeverageConstraints();
            return;
        }

        const minCollateralUsd = collateralDetail.minCollateralUsd;
        store.setLeverageConstraints({
            minCollateralUsd: Number.isFinite(minCollateralUsd)
                ? minCollateralUsd
                : null,
            // upper bound comes from the user's balance + backend tier validation.
            maxCollateralUsd: null,
        });
    }, [collateralDetail]);

    // a running agent setup blocks every trade entry point (buttons AND the L/S shortcuts),
    // since an open placed mid-setup races the agent approval it depends on.
    const agentReactivation = useAgentReactivation();

    // Mounted exactly once, HERE: the agent status hook above is reached from ~8 places, and this
    // one must not fan out into eight Hyperliquid reads. Everything it needs is passed in.
    useAgentPruneDetection({
        adapter: speedBalance.adapter,
        agentAddress: agentReactivation.agentAddress,
        validUntil: agentReactivation.validUntil,
        needsReactivation: agentReactivation.needsReactivation,
        isRealMode: agentReactivation.isRealMode,
        isReactivating: agentReactivation.isReactivating,
    });
    // same single-mount rule: a stale builder fee is repaired silently, never prompted for.
    useBuilderFeeApproval({
        builderApprovalStale: agentReactivation.builderApprovalStale,
        needsReactivation: agentReactivation.needsReactivation,
        isRealMode: agentReactivation.isRealMode,
        isReactivating: agentReactivation.isReactivating,
        agentAddress: agentReactivation.agentAddress,
    });

    // ── Push external hook outputs into the zustand store ────────────────
    useTradingStoreSync({
        runtimeMode,
        // the session key, not the V1 app token: the JWT in V1, `v2:<address>`
        // in V2 while the browser wallet is unlocked. The store only uses it
        // as an enable gate and a query identity.
        token: sessionKey,
        address,
        selectedCoin,
        // the whole per-coin map: every market the hook is driving reaches the
        // store, not just the charted one. An open submitted on ETH must still
        // report its progress while the user watches BTC.
        executionSlots: tradeExecution.slots,
        balance,
        balanceForInputCap,
        custodialAvailable,
        custodialNumericBalance: isRealMode ? speedBalance.numericTotal : null,
        // the spinner is for "no number yet", NOT for "authority still resolving": the balance is
        // shown as soon as the first read lands, dimmed, rather than hidden behind the handshake.
        isCustodialBalanceLoading:
            showsCustodialBalance && speedBalance.isLoading,
        isCustodialBalanceSettling: false,
        // shown but not yet trustworthy for trading: the read succeeded, trading authority has not
        // arrived. rendered dimmed so the number is available immediately without implying the
        // account is ready to trade.
        isCustodialBalanceProvisional:
            showsCustodialBalance &&
            !isRealMode &&
            speedBalance.numericTotal !== null,
        hasPairData: hasMarketData,
        isReactivating: agentReactivation.isReactivating,
        currentValue,
        currentValueCoin,
    });

    // ── Bet clamping (reactive effect) ───────────────────────────────────
    useBetClampEffect();

    // ── Orchestration hooks (read from store, receive only method refs) ──
    // Mirrors the server's active-trades list into the store's per-coin map, so
    // markets this client did not open itself still have a position to show.
    useSyncActiveTradesToStore();
    // …and the exchange's live figures for every one of them, so an off-chart
    // position's PnL and liquidation price are real rather than the client's
    // captured entry. The charted market keeps its own faster poll below.
    useAllLivePositions();

    useTradeNotificationHandler({
        applyTradeUpdate: tradeExecution.applyTradeUpdate,
        applyExecutionPhase: tradeExecution.applyExecutionPhase,
    });

    const clearExecutionPhase = tradeExecution.clearExecutionPhase;
    useEffect(() => {
        clearExecutionPhase();
    }, [selectedCoin, clearExecutionPhase]);

    const { resetReconciliation } = useTradeReconciliation({
        hydrateFromRemoteTrade: tradeExecution.hydrateFromRemoteTrade,
        clearHydratedTrade: tradeExecution.clearHydratedTrade,
    });

    // ── Mode transition ──────────────────────────────────────────────────
    useModeTransitionReset({
        runtimeMode,
        // leaving a V2 session key (`v2:<address>`) is a browser wallet change
        sessionKey,
        resetExecution: tradeExecution.reset,
        resetReconciliation,
    });

    // ── Trade actions ────────────────────────────────────────────────────
    const {
        handleOpenPosition,
        handleClosePosition,
        handleCancelLimitOrder,
        handleFillLimitOrder,
    } = useRealTradeActions({
        tradeExecution,
        balance: { refetch: speedBalance.refetch },
        isolatedOnly: isIsolatedOnly,
    });

    // Demo has no exchange to rest an order on, so this client fills its own
    // limit orders off the same live price feed real mode trades against.
    useDemoLimitFills(currentValue, currentValueCoin);

    // the charted market's resting limit order, drawn on the chart below.
    const restingOrder = useRestingOrder();

    // ── Keyboard shortcuts ───────────────────────────────────────────────
    // Close is deliberately not on the keyboard — see useKeyboardShortcuts.
    useKeyboardShortcuts({ onOpen: handleOpenPosition });

    // ── Live backend position (real mode) — authoritative liq/PnL/margin ──
    const livePosition = useLivePosition();

    // …pushed into the store so ONE set of figures reaches the chart cursor, the
    // positions table and the position panel. Each used to derive its own from
    // whatever it had to hand, which is how the same trade read +$0.20 / +2.43%
    // on the cursor and +$0.27 / +3.29% everywhere else.
    const {
        entryPx: liveEntryPx,
        marginUsed: liveMarginUsed,
        leverage: liveLeverage,
        unrealizedPnl: liveUnrealizedPnl,
        returnOnEquity: liveReturnOnEquity,
        size: liveSize,
        isLong: liveIsLong,
        liquidationPx: liveLiquidationPx,
        hasPosition: liveHasPosition,
    } = livePosition;
    useEffect(() => {
        // useLivePosition polls the SELECTED market, so the snapshot is filed
        // under that coin. Other markets' snapshots arrive from the bulk
        // positions read.
        useTradingStore.getState()._syncLivePosition(
            selectedCoin,
            liveHasPosition
                ? {
                      entryPx: liveEntryPx,
                      marginUsed: liveMarginUsed,
                      leverage: liveLeverage,
                      unrealizedPnl: liveUnrealizedPnl,
                      returnOnEquity: liveReturnOnEquity,
                      // clearinghouseState carries no per-position mark, so
                      // recover the one its PnL was priced at. The price feed's
                      // last price is a different number with a different job.
                      markPrice:
                          liveEntryPx !== null &&
                          liveSize !== null &&
                          liveSize !== 0 &&
                          liveUnrealizedPnl !== null
                              ? liveEntryPx + liveUnrealizedPnl / liveSize
                              : null,
                      // carried so the figure can track the feed between polls
                      // without being recomputed — see realtimePnl.
                      size: liveSize,
                      isLong: liveIsLong,
                      liquidationPx: liveLiquidationPx,
                  }
                : null,
        );
    }, [
        selectedCoin,
        liveHasPosition,
        liveLiquidationPx,
        liveEntryPx,
        liveMarginUsed,
        liveLeverage,
        liveUnrealizedPnl,
        liveReturnOnEquity,
        liveSize,
        liveIsLong,
    ]);

    // ── Store reads for JSX ──────────────────────────────────────────────
    const activeTradeVisual = useTradingStore(selectActiveTradeVisual);
    const chartPulse = useTradingStore((s) => s.chartPulse);
    const takeProfit = useTradingStore((s) => s.takeProfit);
    const stopLoss = useTradingStore((s) => s.stopLoss);

    // Demo has no exchange to ask, so the chart's liq line IS the estimate
    // below — it has to sit where a real liquidation would.
    const chartMaintenanceRate = useMaintenanceRate(
        activeTradeVisual
            ? activeTradeVisual.amount * activeTradeVisual.leverage
            : null,
    );
    // What Hyperliquid is actually resting for this position.
    const openTrade = useOpenTradeForCoin(selectedCoin);
    // Entry / TP / SL / liq levels for the chart's mark lines. TP and SL are
    // the trade's INSTALLED triggers; the armed ROI preset below (converted
    // exactly as the order form arms it) only stands in while there is no trade
    // to read — demo mode, and the window before the fill lands. Liq prefers
    // the backend's authoritative value over the isolated-margin estimate.
    const chartPositionMarks = useMemo<ChartPositionMarks | null>(() => {
        // A resting limit order is the one case with marks but no position: a
        // single neutral line at the price the market has to reach. Nothing
        // else is knowable yet — no entry, no liquidation, no live triggers.
        if (!activeTradeVisual) {
            // a price we never learned draws no line — there is nothing to draw
            if (restingOrder === null || restingOrder.limitPrice === null) {
                return null;
            }
            return {
                type: restingOrder.type,
                entryPrice: restingOrder.limitPrice,
                leverage: restingOrder.leverage ?? 1,
                takeProfitPrice: null,
                stopLossPrice: null,
                liquidationPrice: null,
                limitPrice: restingOrder.limitPrice,
            };
        }
        const { type, entryPrice, leverage: lev } = activeTradeVisual;
        // An armed ROI preset converts against entry; an absolute price is
        // already the line. Both arrive as one store string — see triggerSetting.
        const takeProfitPrice = triggerPriceFrom(
            takeProfit,
            entryPrice,
            lev,
            type,
            true,
        );
        const stopLossPrice = triggerPriceFrom(
            stopLoss,
            entryPrice,
            lev,
            type,
            false,
        );
        const triggers = resolveTriggerLevels(openTrade, {
            takeProfitPrice,
            stopLossPrice,
        });
        return {
            type,
            entryPrice,
            leverage: lev,
            // the position owns the chart once it exists; a limit-origin trade
            // that filled is an ordinary position and draws like one.
            limitPrice: null,
            takeProfitPrice: triggers.takeProfitPrice,
            stopLossPrice: triggers.stopLossPrice,
            liquidationPrice:
                livePosition.liquidationPx ??
                estimateLiquidationPrice(
                    entryPrice,
                    type,
                    lev,
                    chartMaintenanceRate,
                ),
        };
    }, [
        activeTradeVisual,
        restingOrder,
        takeProfit,
        stopLoss,
        livePosition.liquidationPx,
        chartMaintenanceRate,
        openTrade,
    ]);

    // ── Liquidation map (market-wide, public) ────────────────────────────
    // Bands on the chart + the legend that explains them. Off by default and
    // opt-in from the chart toolbar: the map is a tool to reach for, not a
    // layer every trader gets handed. Persisted per browser once chosen.
    const [liqShown, setLiqShownState] = useState<boolean>(() => {
        try {
            return window.localStorage.getItem(LIQ_MAP_STORAGE_KEY) === '1';
        } catch {
            return false;
        }
    });
    const setLiqShown = useCallback((next: boolean) => {
        setLiqShownState(next);
        try {
            window.localStorage.setItem(LIQ_MAP_STORAGE_KEY, next ? '1' : '0');
        } catch {
            // storage unavailable — the choice just won't persist
        }
    }, []);
    // no polling at all while the zones are off
    const liqMap = useLiqMap(selectedCoin, liqShown);
    const liqBands = useMemo(
        () => (liqShown ? bandsFor(liqMap.snapshot) : []),
        [liqShown, liqMap.snapshot],
    );
    // the trader's own levels, for the "your stop is inside a cluster" check
    const liqTrade = useMemo(() => {
        if (!chartPositionMarks) return null;
        return {
            type: chartPositionMarks.type,
            stopLossPrice: chartPositionMarks.stopLossPrice,
            liquidationPrice: chartPositionMarks.liquidationPrice,
        };
    }, [chartPositionMarks]);

    // ── Support / resistance (client-side, from the browser's own candles) ──
    // Off on every load and NOT remembered (user decision 2026-09-10): the
    // trader opens it from the chart toolbar when they want it, and a reload
    // starts clean — unlike liq zones, which persist. The zones are computed
    // once per candle refresh; only orienting them against the live price is
    // re-done, on a 0.1%-quantised spot, so a price tick does not recompute
    // three days of swings.
    const [srShown, setSrShown] = useState(false);
    // Overlays are per pair (user decision 2026-09-10): switching coins turns
    // both off; the trader opts in again on the new pair.
    const hideOverlays = useCallback(() => {
        setSrShown(false);
        setLiqShown(false);
    }, [setLiqShown]);
    useResetOnPairSwitch(selectedCoin, hideOverlays);
    // A limit price belongs to ONE market: carrying $92,000 from BTC to a $3
    // coin arms a price that can never be reached (and, on the other side, one
    // that fills instantly). The armed ORDER TYPE is a policy and stays.
    const clearLimitPrice = useCallback(() => {
        useTradingStore.getState().setLimitPrice('');
    }, []);
    useResetOnPairSwitch(selectedCoin, clearLimitPrice);
    const candles = useCandles(selectedCoin, srShown);
    const srZones = useMemo(
        () =>
            srShown
                ? computeZones(candles.bars, candles.updatedAt || Date.now())
                : [],
        [srShown, candles.bars, candles.updatedAt],
    );
    const srSpot = quantiseSpot(targetValue);
    const srLevels = useMemo(
        () => orientLevels(srZones, srSpot),
        [srZones, srSpot],
    );
    const srTrade = useMemo(() => {
        if (!chartPositionMarks) return null;
        return {
            type: chartPositionMarks.type,
            stopLossPrice: chartPositionMarks.stopLossPrice,
        };
    }, [chartPositionMarks]);

    return (
        <div className="bento-grid text-foreground">
            <Header />

            {/* Chart header strip (pair select + price + 24h change + chart
                type) — sits in the `stats` grid area at every width. */}
            <SpeedChartHeader
                currentValue={currentValue}
                priceFlash={priceFlash}
                priceConfig={priceConfig}
                chartMode={chartMode}
                onChartModeChange={setChartMode}
                liqShown={liqShown}
                onToggleLiq={setLiqShown}
                srShown={srShown}
                onToggleSr={setSrShown}
            />

            {/* Markets Sidebar - Wide desktop only. */}
            {showRail && <MarketsSidebar />}

            {/* Chart Cell */}
            <Panel
                as="main"
                className={`relative bg-[#050605] ${chartPulse === 'win' ? 'chart-border-pulse-win' : chartPulse === 'loss' ? 'chart-border-pulse-loss' : ''}`}
                style={{ gridArea: 'chart' }}
            >
                <div className="flex h-full flex-col">
                    <div className="relative min-h-0 flex-1">
                        {showCandles ? (
                            <TradingViewChart
                                coin={selectedCoin}
                                livePrice={targetValue}
                                position={chartPositionMarks}
                                liqBands={liqBands}
                                liqShown={liqShown}
                                onToggleLiq={setLiqShown}
                                srLevels={srLevels}
                                srShown={srShown}
                                onToggleSr={setSrShown}
                            />
                        ) : (
                            <TrendChart
                                targetPrice={targetValue}
                                activePosition={activeTradeVisual}
                                onCurrentValueChange={handleCurrentValueChange}
                                tokenPair={selectedPair
                                    .replace('-', '/')
                                    .toUpperCase()}
                                displayPrice={currentValue}
                                priceFlash={priceFlash}
                                connectionState={connectionState}
                                isStale={isStale}
                                liquidationPrice={livePosition.liquidationPx}
                                positionLevels={chartPositionMarks}
                                liqBands={liqBands}
                                srLevels={srLevels}
                            />
                        )}
                        {/* Price + PnL pip. The live chart draws it INSIDE
                            the SVG; candles has no SVG surface, so it uses
                            the HTML overlay pip here. */}
                        {showCandles && <PnlTag priceConfig={priceConfig} />}
                        <SpeedResultFx priceConfig={priceConfig} />
                        {/* Liquidation zones legend: bottom-left, clear of the
                            price axis on the right and the TV logo's corner.
                            Only while the bands are on — the toggle lives in
                            the chart toolbar, and a legend for hidden bands
                            explains nothing. */}
                        {(liqShown || srShown) && (
                            <div className="pointer-events-none absolute bottom-9 left-2 z-10 flex flex-col gap-1.5 sm:bottom-10 sm:left-3">
                                {liqShown && (
                                    <LiqMapLegend
                                        coin={selectedCoin}
                                        snapshot={liqMap.snapshot}
                                        trade={liqTrade}
                                    />
                                )}
                                {srShown && (
                                    <SrLevelsLegend
                                        coin={selectedCoin}
                                        levels={srLevels}
                                        spot={srSpot}
                                        barCount={candles.bars.length}
                                        updatedAt={candles.updatedAt}
                                        isLoading={candles.isLoading}
                                        isError={candles.isError}
                                        trade={srTrade}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Panel>

            {/* Trade rail (order form + open position) - Desktop only. */}
            {!isMobile && (
                <Panel
                    as="aside"
                    aria-label="Trade panel"
                    className="flex flex-col overflow-y-auto"
                    style={{ gridArea: 'panel' }}
                >
                    {/* the account banners (V1's, or V2's browser wallet
                        banner) render inside the rail */}
                    <SpeedTradeRail
                        currentValue={currentValue}
                        priceConfig={priceConfig}
                        onOpenPosition={handleOpenPosition}
                        onClosePosition={handleClosePosition}
                        onCancelLimitOrder={handleCancelLimitOrder}
                        onFillLimitOrder={handleFillLimitOrder}
                        isIsolatedOnly={isIsolatedOnly}
                    />
                </Panel>
            )}

            {/* Mobile Controls - Mobile only */}
            {isMobile && (
                <Panel
                    as="section"
                    aria-label="Trade controls"
                    className="flex flex-col p-2"
                    style={{ gridArea: 'controls' }}
                >
                    <UntrackedExposureCard className="mb-2" />
                    <MobileControlPanel
                        currentValue={currentValue}
                        priceConfig={priceConfig}
                        onOpenPosition={handleOpenPosition}
                        onClosePosition={handleClosePosition}
                        onCancelLimitOrder={handleCancelLimitOrder}
                        onFillLimitOrder={handleFillLimitOrder}
                        isIsolatedOnly={isIsolatedOnly}
                    />
                </Panel>
            )}

            {/* POSITIONS / HISTORY table - Desktop only. The untracked-exposure
                warning sits directly above it: money the exchange is holding
                that no row explains belongs next to the rows, not in a corner. */}
            {!isMobile && (
                <div
                    className="flex min-w-0 flex-col gap-2"
                    style={TERMINAL_GRID_STYLE}
                >
                    <UntrackedExposureCard />
                    <SpeedTerminal
                        connectionState={connectionState}
                        latency={latency}
                        isStale={isStale}
                        priceConfig={priceConfig}
                        onClosePosition={handleClosePosition}
                        onCancelLimitOrder={handleCancelLimitOrder}
                        onFillLimitOrder={handleFillLimitOrder}
                    />
                </div>
            )}

            {/* ⌘K command palette */}
            <CommandPalette onClosePosition={handleClosePosition} />

            {/* Auth Error Notification */}
            <AuthErrorBanner error={authError} />

            {isBrowserWalletFlow ? (
                <Suspense fallback={null}>
                    {/* Browser wallet funds (deposit / withdraw / wallet) */}
                    <BrowserWalletPanel />
                    {/* Restore, set up, back up, manage backups */}
                    <BrowserWalletDialog />
                </Suspense>
            ) : (
                <>
                    {/* Quick Trade Wallet Panel */}
                    <CustodialPanelContainer />

                    {/* First-login onboarding (welcome → terms → wallet setup → funding) */}
                    <OnboardingGate />
                </>
            )}

            {/* Trade Result Overlay - Screen edge glow effect */}
            <TradeResultOverlay />

            {/* Held win/loss viewport frame while a position is open */}
            <WinFrame />
        </div>
    );
};

export default Trading;
